import type { ScreenerCandidateDto } from '@/api/kit/KitApi'

export type ScoreType = 'binary' | 'pass-flag-fail' | 'numeric-1-3'

export type RubricCriterion = {
    id: string
    label: string
    shortName: string | null
    scoreType: ScoreType
}

export type RubricSection = {
    id: string
    label: string
    criteria: RubricCriterion[]
}

export type Rubric = {
    sections: RubricSection[]
}

export type BinaryScore = 'pass' | 'fail'
export type PassFlagFailScore = 'pass' | 'flag' | 'fail'
export type NumericScore = 1 | 2 | 3
export type ScoreValue =
    | BinaryScore
    | PassFlagFailScore
    | NumericScore
    | 'not_asked'
    | 'not_answered'
    | null

/** A score with an optional detail string (e.g. "£65,000", "3 months") */
export type ScoreEntry = {
    value: ScoreValue
    detail?: string
}

/** Map from criterionId -> score entry (or plain ScoreValue for backwards compat) */
export type CandidateScores = Record<string, ScoreValue | ScoreEntry>

/** Helper to normalise a score record entry into { value, detail } */
export function normaliseScore(
    entry: ScoreValue | ScoreEntry | undefined,
): ScoreEntry {
    if (entry === null || entry === undefined) {
        return { value: null }
    }
    if (typeof entry === 'object' && 'value' in entry) {
        return entry
    }
    return { value: entry }
}

export type Candidate = {
    id: string
    name: string
    phone: string | null
    email: string | null
    linkedInUrl: string | null
    profilePicUrl: string | null
    overallPercentage: number | null
    scores: CandidateScores
    hasScores: boolean
}

/** Convert API ScreenerCandidateDto to local Candidate type */
export function mapApiCandidate(dto: ScreenerCandidateDto): Candidate {
    const scores: CandidateScores = {}
    for (const [criterionId, scoreData] of Object.entries(dto.scores)) {
        if (scoreData.value === null) {
            scores[criterionId] = null
        } else if (
            scoreData.value === 'not_asked' ||
            scoreData.value === 'not_answered'
        ) {
            // New score states: return as-is before parseInt (which would produce NaN)
            scores[criterionId] = scoreData.detail
                ? { value: scoreData.value, detail: scoreData.detail }
                : scoreData.value
        } else {
            // Parse score value: 'pass','fail','flag' stay as strings; '1','2','3' become numbers
            const numVal = parseInt(scoreData.value, 10)
            const parsedValue =
                !isNaN(numVal) && numVal >= 1 && numVal <= 3
                    ? (numVal as NumericScore)
                    : (scoreData.value as BinaryScore | PassFlagFailScore)
            scores[criterionId] = scoreData.detail
                ? { value: parsedValue, detail: scoreData.detail }
                : parsedValue
        }
    }
    return {
        id: dto.id,
        name: dto.name,
        phone: dto.phone,
        email: dto.email,
        linkedInUrl: dto.linkedInUrl,
        profilePicUrl: dto.profilePicUrl,
        overallPercentage: dto.overallPercentage,
        scores,
        hasScores: dto.hasScores,
    }
}
